<script lang="ts">

  import { goto } from "$app/navigation"
  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import Loader from "$lib/components/loader.svelte"
  import { Button } from "$lib/components/ui/button"
  import { Calendar } from "$lib/components/ui/calendar"
  import { SeparatorLabel } from "$lib/components/ui/separator-label"
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from "$lib/components/ui/command"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import { Popover, PopoverButton, PopoverContent } from "$lib/components/ui/popover"
  import { RadioGroup, RadioGroupItem } from "$lib/components/ui/radio-group"
  import { Separator } from "$lib/components/ui/separator"
  import { Textarea } from "$lib/components/ui/textarea"
  import type { _response } from "$lib/data/type/_response"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { getLocalTimeZone, today, type DateValue } from "@internationalized/date"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import XIcon from "@lucide/svelte/icons/x"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"
  import DialogConfirmDelete from "../_components/dialog-confirm-delete.svelte"
  import { type Activity, type Relation } from "../_types"

  const FormSchema = z.object({
    id: z.number().default(0),
    activity_id: z.number().default(0),
    activity_name: z.string().default(""),
    number: z.string().default(""),
    date: z.custom<DateValue | null>().default(null),
    category_id: z.number().default(0),
    category_name: z.string().default(""),
    customer_type_id: z.number().default(0),
    customer_type_name: z.string().default(""),
    customer_id: z.number().default(0),
    customer_name: z.string().default(""),
    is_other: z.number().default(0),
    name: z.string().default(""),
    description: z.string().default(""),
    at_start: z.string().default(""),
    at_end: z.string().default(""),
    stage_id: z.number().default(0),
    stage_name: z.string().default(""),
  })
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({})

  const prefix = $derived(page.params.prefix ?? "0")
  const select = $derived(page.params.id ?? "create")
  const isEdit = $derived(select !== "create")

  const deleteDialog = useDialog()

  const form = createForm(() => ({
    defaultValues: FormEmpty,
  }))
  const formWatch = form.useSelector((state) => state.values)

  const categories = createQuery(() => ({
    queryKey: ["activity-unplan-category", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/unplans/master-category`)
      return response.data.result ?? []
    },
  }))
  const customerTypes = createQuery(() => ({
    queryKey: ["activity-unplan-customer-type", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/unplans/master-customer-type`)
      return response.data.result ?? []
    },
  }))
  const customerSearch = createSearch()
  const customers = createQuery(() => ({
    queryKey: ["activity-unplan-customer", {
      prefix,
      customer_type_id: formWatch.current.customer_type_id,
      search: customerSearch.debounced,
    }],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/unplans/master-customer`, {
        params: {
          type_id: formWatch.current.customer_type_id,
          search: customerSearch.debounced
        },
      })
      return response.data.result ?? []
    },
  }))
  const stages = createQuery(() => ({
    queryKey: ["activity-unplan-stage", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/unplans/master-stage`)
      return response.data.result ?? []
    },
  }))

  const query = createQuery(() => ({
    queryKey: ["activity-unplan-detail", prefix, select],
    queryFn: async () => {

      const id = select == "" ? "create" : select
      const response = await httpClient.get<_response<Activity>>(`/api/${prefix}/activity/unplans/${id}`)
      const responseData = response.data.result!

      form.reset(FormEmpty)
      form.setFieldValue("id", responseData.id)
      form.setFieldValue("activity_id", responseData.activity.id)
      form.setFieldValue("activity_name", responseData.activity.name)
      form.setFieldValue("number", responseData.number)
      form.setFieldValue("date", responseData.date.toDateTime())
      form.setFieldValue("category_id", responseData.category.id)
      form.setFieldValue("category_name", responseData.category.name)
      form.setFieldValue("customer_type_id", responseData.customer_type.id)
      form.setFieldValue("customer_type_name", responseData.customer_type.name)
      form.setFieldValue("customer_id", responseData.customer.id)
      form.setFieldValue("customer_name", responseData.customer.name)
      form.setFieldValue("is_other", responseData.is_other ? 1 : 0)
      form.setFieldValue("name", responseData.name)
      form.setFieldValue("description", responseData.description)
      form.setFieldValue("at_start", responseData.at_start.toDateTime().format("HH:mm:ss"))
      form.setFieldValue("at_end", responseData.at_end.toDateTime().format("HH:mm:ss"))
      form.setFieldValue("stage_id", responseData.stage.id)
      form.setFieldValue("stage_name", responseData.stage.name)

      return ""
    },
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {

      const id = select == "create" ? "" : select
      const response = await httpClient.postForm<_response<null>>(`/api/${prefix}/activity/unplans/${id}`, {
        _method: id == "" ? "POST" : "PATCH",
        ...data,
        date: data.date?.format("yyyy-MM-dd"),
      })
      const responseData = response.data

      form.reset(data)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (previous) => ({
          ...previous,
          errorMap: { onServer: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) {
        return
      }

      toast.success(responseData.message)
      await goto(`/${prefix}/activity-unplan`, { replaceState: true })

      return
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message,
      })
    },
  }))

</script>

<Loader
  show={
    query.isFetching ||
    mutation.isPending ||
    categories.isFetching ||
    customerTypes.isFetching ||
    stages.isFetching
  }
  className="z-20"/>
<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/activity-unplan`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full">
          <XIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Tidak Terencana Form</h1>
      </div>
      <div></div>
    </div>
  </nav>
  <div class="h-4"></div>
  <form
    action="#"
    autocomplete="off"
    class="px-3 space-y-3"
    onsubmit={(event) => {
      event.preventDefault()
      mutation.mutate(formWatch.current)
    }}>
    <div class="flex gap-3">
      <div class="flex-1">
        <form.Field name="number">
          {#snippet children(field)}
            <div>
              <FieldLabel>Nomor</FieldLabel>
              <Input
                name={field.name}
                value={field.state.value}
                readonly
                class="font-mono"/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
      <div class="flex-1"></div>
    </div>
    <form.Field name="customer_type_id">
      {#snippet children(field)}
        <div>
          <FieldLabel>Tipe Customer</FieldLabel>
          <RadioGroup
            name={field.name}
            value={String(field.state.value)}
            class="flex gap-4">
            {#each customerTypes.data ?? [] as item (item.id)}
              <label class="flex items-center gap-2 text-sm">
                <RadioGroupItem
                  value={String(item.id)}
                  onclick={() => {
                    field.handleChange(item.id)
                    form.setFieldValue("customer_type_name", item.name)
                    form.setFieldValue("customer_id", 0)
                    form.setFieldValue("customer_name", "")
                  }}
                />
                <span>{item.name}</span>
              </label>
            {/each}
          </RadioGroup>
          <FieldMessage
            name={field.name}
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <form.Field name="customer_name">
      {#snippet children(field)}
        <div>
          <FieldLabel>{formWatch.current.customer_type_name || "Customer"}</FieldLabel>
          <Popover>
            <PopoverButton value={field.state.value} placeholder="Pilih Data"/>
            <PopoverContent>
              <Command shouldFilter={false}>
                <CommandInput placeholder="Cari..." value={customerSearch.state} oninput={(event) => customerSearch.set(event.currentTarget.value)}/>
                <CommandList class="min-h-20">
                  <CommandEmpty>Tidak ada data</CommandEmpty>
                  <CommandGroup>
                    {#each customers.data ?? [] as item (item.id)}
                      <CommandItem value={item.name} onclick={() => { form.setFieldValue("customer_id", item.id); field.handleChange(item.name); document.keyEsc() }}>{item.name}</CommandItem>
                    {/each}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FieldMessage name="customer_id" form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <div class="h-[1px]"></div>
    <SeparatorLabel label="Rencana"/>
    <form.Field name="is_other">
      {#snippet children(field)}
        <div>
          <FieldLabel>Aktifitas Lain</FieldLabel>
          <RadioGroup
            name={field.name}
            value={String(field.state.value)}
            class="flex gap-4">
            <label class="flex items-center gap-2 text-sm">
              <RadioGroupItem
                value="1"
                onclick={() => field.handleChange(1)}
              />
              <span>Ya</span>
            </label>
            <label class="flex items-center gap-2 text-sm">
              <RadioGroupItem
                value="0"
                onclick={() => field.handleChange(0)}
              />
              <span>Tidak</span>
            </label>
          </RadioGroup>
          <FieldMessage name={field.name} form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <form.Field name="category_name">
      {#snippet children(field)}
        <div>
          <FieldLabel>Kategori</FieldLabel>
          <Popover>
            <PopoverButton
              value={field.state.value}
              placeholder="Pilih Kategori"/>
            <PopoverContent>
              <Command>
                <CommandList class="min-h-20">
                  <CommandEmpty>Tidak ada data</CommandEmpty>
                  <CommandGroup>
                    {#each categories.data ?? [] as item (item.id)}
                      <CommandItem
                        value={item.name}
                        onclick={() => {
                          form.setFieldValue("category_id", item.id)
                          field.handleChange(item.name)
                          document.keyEsc()
                        }}>
                        {item.name}
                      </CommandItem>
                    {/each}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FieldMessage
            name="category_id"
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <form.Field name="name">
      {#snippet children(field)}
        <div>
          <FieldLabel>Aktifitas</FieldLabel>
          <Input
            name={field.name}
            value={field.state.value}
            oninput={(event) => field.handleChange(event.currentTarget.value)}/>
          <FieldMessage
            name={field.name}
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <form.Field name="description">
      {#snippet children(field)}
        <div>
          <FieldLabel>Keterangan</FieldLabel>
          <Textarea
            name={field.name}
            value={field.state.value}
            oninput={(event) => field.handleChange(event.currentTarget.value)}/>
          <FieldMessage
            name={field.name}
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <div class="flex gap-3">
      <div class="flex-1">
        <form.Field name="date">
          {#snippet children(field)}
            <div>
              <FieldLabel>Tanggal</FieldLabel>
              <Popover>
                <PopoverButton
                  class="font-normal"
                  value={field.state.value?.format("dd MMMM yyyy") ?? ""}/>
                <PopoverContent align="start">
                  <Calendar
                    type="single"
                    value={field.state.value ?? undefined}
                    maxValue={today(getLocalTimeZone())}
                    onValueChange={(value) => {
                      if (!value) return
                      form.setFieldValue("date", value)
                      document.keyEsc()
                    }}/>
                </PopoverContent>
              </Popover>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
      <div class="flex-1"></div>
    </div>
    <div class="flex gap-3">
      <div class="flex-1">
        <form.Field name="at_start">
          {#snippet children(field)}
            <div>
              <FieldLabel>Waktu Mulai</FieldLabel>
              <Input
                type="time"
                class="tabular-nums tracking-tight"
                value={field.state.value.slice(0, 5)}
                oninput={(event) => field.handleChange(`${event.currentTarget.value}:00`)}/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
      <div class="flex-1">
        <form.Field name="at_end">
          {#snippet children(field)}
            <div>
              <FieldLabel>Waktu Selesai</FieldLabel>
              <Input
                type="time"
                class="tabular-nums tracking-tight"
                value={field.state.value.slice(0, 5)}
                oninput={(event) => field.handleChange(`${event.currentTarget.value}:00`)}/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
    </div>
    <form.Field name="stage_name">
      {#snippet children(field)}
        <div>
          <FieldLabel>Tahap</FieldLabel>
          <Popover>
            <PopoverButton
              value={field.state.value}
              placeholder="Pilih Tahapan"/>
            <PopoverContent>
              <Command>
                <CommandList class="min-h-20">
                  <CommandEmpty>Tidak ada data</CommandEmpty>
                  <CommandGroup>
                    {#each stages.data ?? [] as item (item.id)}
                      <CommandItem
                        value={item.name}
                        onclick={() => {
                          form.setFieldValue("stage_id", item.id)
                          field.handleChange(item.name)
                          document.keyEsc()
                        }}>
                        {item.name}
                      </CommandItem>
                    {/each}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FieldMessage
            name="stage_id"
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <Separator/>
    <div class="flex justify-between gap-2">
      <Button
        type="button"
        variant="destructive"
        disabled={!isEdit}
        onclick={() => deleteDialog.setOpen(true)}>
        Hapus
      </Button>
      <div class="flex-1"></div>
      <Button
        type="submit"
        class="group"
        data-loading={mutation.isPending}
        disabled={mutation.isPending}>
        <LoaderCircleIcon class="invisible absolute animate-spin group-data-[loading=true]:visible"/>
        <span class="group-data-[loading=true]:invisible">Simpan</span>
      </Button>
    </div>
    <div class="h-20"></div>
  </form>
</main>
<DialogConfirmDelete
  open={deleteDialog.state}
  setOpen={deleteDialog.setOpen}
  select={select}
  onDeleted={() => void goto(`/${prefix}/activity-unplan`, { replaceState: true })}/>
